import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { StripeCardModification, StripeSource } from '../models';
import PaymentIntent from '@app/models/payment-intent';
import { CustomerCard } from '@app/models-internal/customer-card';

@Injectable()
export class CardService {
  private readonly _serviceName = 'CardService';

  constructor(private http: HttpClient) {}

  /**
   * The current user's saved cards from the Stripe PaymentMethods API, default first. Includes cards saved by the
   * Payment Element (pm_…) and legacy cards (card_…).
   */
  public getPaymentMethods() {
    return this.http.get<CustomerCard[]>('card/payment-methods');
  }

  /**
   * Removes one of the current user's saved cards.
   * @param cardId Stripe id, pm_… or card_….
   */
  public delete(cardId: string) {
    if (!cardId) {
      throw new Error('Parameter cardId is null/empty.');
    }

    return this.http.delete<boolean>(`card/${encodeURIComponent(cardId)}`);
  }

  public update(updateObj: StripeCardModification) {
    console.log(`${this._serviceName}.update`, updateObj);

    return this.http.post('stripe/card/update', updateObj);
  }

  /**
   * Add a new credit card to the current user's account
   * @param c
   */
  public add(c: StripeSource) {
    // get id to add to stripe
    const stripeSourceId = { id: c.stripeSourceId };
    return this.http.post<StripeSource>('card/', stripeSourceId);
  }

  /**
   * Sets a user's default card
   * @param cardId
   */
  public setDefault(cardId: string) {
    if (!cardId) {
      throw new Error('Parameter cardId is null/empty.');
    }

    return this.http.post('card/default', JSON.stringify(cardId));
  }

  public createPaymentIntent(paymentIntent: PaymentIntent) {
    return this.http.post<string>('stripe/createpi', paymentIntent);
  }

  public createCustomerSession() {
    return this.http.post<string>('stripe/createCustomerSession', null);
  }

  /**
   * Creates a Stripe subscription with first invoice payment intent.
   * Used when offer contains membership line (type 5).
   * @param request - Subscription details including recurring amount, one-time charges, and offer metadata
   * @returns Observable with clientSecret for Payment Element
   */
  public createSubscriptionWithPaymentIntent(request: CreateAnnualSubscriptionRequest) {
    return this.http.post<{ clientSecret: string }>('stripe/createAnnualSubscription', request);
  }
}

export interface CreateAnnualSubscriptionRequest {
  oneTimeAmount: number; // Sum of line types 1-4
  currency: string;
  offerId: string;
  listingId: number;
}
